import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Sparkles,
  BookOpen, Lightbulb, Baby, Puzzle, NotebookPen, GraduationCap, Feather, Clapperboard, Wand2,
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/MarketingSite";
import { FoundingOffer } from "@/components/FoundingOffer";

const BASE = `${process.env.PUBLIC_URL}/samples/`;
const IMG = {
  horizon: BASE + "3228f6ae64deca48864aac73adabbaf7f890b32b5e5ca19d60add104c630eb85.jpeg",
  mindset: BASE + "fd09b1a974a2e9ea630a467c10e5dd93a3f4a84bf837bdda5f96089face32778.jpeg",
  letters: BASE + "07a4af0cc05d6f60411b88fe2c62fba31db4d42bc0b0c1ee44c317083cba6ee7.jpeg",
  luna: BASE + "c25316ce7c9e70a76837f8117edb6294b8cd8cede0b9cff1588bddb2cada1a02.jpeg",
  ocean: BASE + "7eed9b7513337905fe308a0c5179682d8b43794c9c8afb7c829322f2b33b2d5d.jpeg",
  meals: BASE + "2fec99ea10693800f6e455521d14054af1628bde47dbf8d23aff5d6c6b752b94.jpeg",
  puzzles: BASE + "cc065efb21438712a5ad2a583d2e7e13f5f502820c78b219ad7b5e9d33200d85.jpeg",
};

const GALLERY = [
  { src: IMG.horizon, title: "The Last Horizon", tag: "Literary Thriller" },
  { src: IMG.luna, title: "Luna's Brave Heart", tag: "Children's" },
  { src: IMG.mindset, title: "The Mindset Shift", tag: "Non-fiction" },
  { src: IMG.letters, title: "Letters to the Sea", tag: "Romance" },
  { src: IMG.meals, title: "Simple Healthy Meals", tag: "Cookbook" },
  { src: IMG.ocean, title: "Ocean Adventures", tag: "Activity" },
  { src: IMG.puzzles, title: "Brain Boost Puzzles", tag: "Puzzle" },
];


const ARC = [
  { phase: "01 · The Dream", title: "The Unspoken Spark", desc: "You carry an idea, a concept, a memory, or a vision that quietly demands to be written." },
  { phase: "02 · The Friction", title: "The Blank Canvas", desc: "Writing, formatting, cover design, and KDP technicalities stall the dream before it even starts." },
  { phase: "03 · The Transformation", title: "The KLYR Synthesis", desc: "AI intelligence meets master editorial craft. Words, artwork, and formatting crystallize into alignment." },
  { phase: "04 · The Result", title: "A Masterpiece In Hand", desc: "A complete, beautifully formatted manuscript with a custom cover wrap — ready for Amazon KDP and print." },
  { phase: "05 · The Invitation", title: "Your Author Journey Begins", desc: "Claim your seat in the studio and bring your book into the world today." },
];

const GENRES = [
  { icon: BookOpen, badge: "Fiction", name: "Literary & Genre Fiction", desc: "Sweeping novels with rich multi-chapter arcs, character continuity, and cinematic covers." },
  { icon: Lightbulb, badge: "Nonfiction", name: "Nonfiction & Thought Leadership", desc: "Research-backed guides, self-help, frameworks, APA citations, and actionable workbooks." },
  { icon: Baby, badge: "Children's", name: "Children's Picture Books", desc: "Full-color custom illustrated picture books with emotional storytelling on every spread." },
  { icon: Puzzle, badge: "Interactive", name: "Puzzle & Activity Books", desc: "Auto-generated Sudoku, mazes, word searches, and answer keys — print-perfect 8.5×11." },
  { icon: NotebookPen, badge: "Wellness", name: "Guided Journals & Planners", desc: "Structured prompt pages, reflection layouts, daily trackers, and elegant typography." },
  { icon: GraduationCap, badge: "Education", name: "Workbooks & Study Guides", desc: "Lesson structures, key-takeaway boxes, fill-in exercises, and printable PDF layouts." },
  { icon: Feather, badge: "Editorial", name: "Poetry & Anthologies", desc: "Delicate typesetting, spacious stanza layouts, expressive headers, and artistic accents." },
  { icon: Clapperboard, badge: "Visual", name: "Cinematic & Illustrated Books", desc: "Immersive graphic-driven storytelling with generated concept art on every chapter opener." },
  { icon: Wand2, badge: "Next-Gen", name: "AI-Enhanced Hybrid Formats", desc: "Read-along audio narration, digital EPUB + print-wrap bundles, and social marketing kits." },
];

const PARTICLES = [
  { x: "8%", y: "18%", d: 0, s: 6 }, { x: "22%", y: "62%", d: 1.2, s: 4 },
  { x: "40%", y: "12%", d: 0.6, s: 5 }, { x: "70%", y: "24%", d: 1.8, s: 7 },
  { x: "85%", y: "55%", d: 0.3, s: 5 }, { x: "60%", y: "70%", d: 2.1, s: 4 },
  { x: "33%", y: "82%", d: 1.5, s: 6 }, { x: "92%", y: "30%", d: 0.9, s: 4 },
  { x: "15%", y: "40%", d: 2.4, s: 5 }, { x: "50%", y: "45%", d: 1.0, s: 6 },
];

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const GalleryCard = ({ item }) => (
  <div className="relative shrink-0 w-[180px] sm:w-[210px] mx-3 group">
    <div className="relative rounded-xl overflow-hidden border border-gold-500/20 shadow-[0_24px_50px_-18px_rgba(0,0,0,0.85)] transition-transform duration-500 group-hover:-translate-y-2">
      <img src={item.src} alt={`${item.title} — ${item.tag}`} className="w-full aspect-[2/3] object-cover" loading="lazy" />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050915] via-[#050915]/60 to-transparent p-3 pt-8">
        <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-gold-300">{item.tag}</div>
        <div className="font-display text-sm text-slate-100 leading-tight">{item.title}</div>
      </div>
    </div>
  </div>
);

export const LandingPage = ({ onGetStarted, onSignIn, onNavigate }) => {
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bookY = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);
  const glowScale = useTransform(scrollYProgress, [0, 1], [1, 1.35]);

  const navHome = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const handleNav = (k) => {
    if (k === "features") { document.getElementById("possible")?.scrollIntoView({ behavior: "smooth" }); return; }
    onNavigate(k);
  };
  const floatAnim = reduce ? {} : { y: [0, -16, 0], transition: { duration: 7, repeat: Infinity, ease: "easeInOut" } };

  return (
    <div className="min-h-screen bg-[#050915] text-slate-100 overflow-x-hidden">
      <MarketingNav current="home" onHome={navHome} onNavigate={handleNav} onGetStarted={onGetStarted} onSignIn={onSignIn} />

      {/* ============================================================= HERO */}
      <section ref={heroRef} className="relative min-h-[640px] lg:min-h-[720px] flex items-center overflow-hidden">
        {/* parallax ambient background */}
        <motion.div style={{ y: reduce ? 0 : bgY }} className="absolute inset-0 -z-10">
          <img src={IMG.horizon} alt="" aria-hidden className="absolute right-0 top-0 h-full w-[70%] object-cover opacity-[0.10] blur-[2px]" />
          <div className="absolute inset-0 bg-[#050915]" style={{ background: "linear-gradient(to bottom, rgba(5,9,21,0.4), #050915 85%)" }} />
        </motion.div>
        <motion.div style={{ scale: reduce ? 1 : glowScale }}
          className="pointer-events-none absolute -z-10 top-1/2 right-[18%] -translate-y-1/2 w-[620px] h-[620px] rounded-full"
          aria-hidden
        >
          <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(ellipse at center, rgba(229,193,88,0.28) 0%, rgba(13,24,54,0) 70%)" }} />
        </motion.div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-8 py-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          {/* copy */}
          <div>
            <motion.div variants={fade} initial="hidden" animate="show" custom={0}
              className="flex items-center gap-2 font-ui text-[11px] sm:text-xs uppercase tracking-[0.34em] text-gold-400 font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Where Imagination Takes Physical Form
            </motion.div>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="font-display font-semibold tracking-tight leading-[1.07] text-4xl sm:text-5xl lg:text-6xl text-slate-50">
              Every Story Deserves More Than an Idea.{" "}
              <span className="gold-text italic font-normal">It Deserves KLYR.</span>
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-6 font-read text-lg text-slate-300/90 leading-relaxed max-w-xl">
              Step inside a cinematic studio that turns your wildest ideas into publish-ready hardcovers,
              paperbacks, and digital masterpieces — in minutes, not months.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap items-center gap-4">
              <button data-testid="hero-start-button" onClick={onGetStarted}
                className="group gold-pill rounded-full px-8 py-4 font-ui text-base inline-flex items-center gap-2">
                Begin Your Book
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button data-testid="hero-examples-button" onClick={() => handleNav("examples")}
                className="font-ui text-base text-slate-200 border border-gold-500/25 hover:border-gold-400 rounded-full px-7 py-4 transition-colors">
                See the Gallery
              </button>
            </motion.div>
            <motion.div variants={fade} initial="hidden" animate="show" custom={4}
              className="mt-7 font-ui text-sm text-slate-500">
              No credit card · 3 free book previews · Your words, your rights.
            </motion.div>
          </div>

          {/* book forming from light */}
          <motion.div style={{ y: reduce ? 0 : bookY }} className="relative hidden lg:flex justify-center items-center h-[520px]">
            {/* floating manuscript pages */}
            {!reduce && [0, 1, 2].map((i) => (
              <motion.div key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.15, 0.4, 0.15], y: [0, -24, 0], rotate: [-8 + i * 6, -4 + i * 6, -8 + i * 6] }}
                transition={{ duration: 8 + i * 1.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }}
                className="absolute w-36 h-48 rounded-md bg-[#fdf6e3]/80 shadow-2xl"
                style={{ left: `${20 + i * 24}%`, top: `${8 + i * 10}%` }} aria-hidden />
            ))}
            {/* particle sparks */}
            {!reduce && PARTICLES.map((p, i) => (
              <motion.span key={i}
                className="absolute rounded-full bg-gold-300"
                style={{ left: p.x, top: p.y, width: p.s, height: p.s, boxShadow: "0 0 10px rgba(229,193,88,0.9)" }}
                animate={{ opacity: [0, 1, 0], scale: [0.6, 1.2, 0.6], y: [0, -18, 0] }}
                transition={{ duration: 3.2 + p.s * 0.2, repeat: Infinity, ease: "easeInOut", delay: p.d }} aria-hidden />
            ))}
            {/* the forming book */}
            <motion.div animate={floatAnim}
              className="relative z-10 w-[280px] rounded-xl overflow-hidden border border-gold-400/40 shadow-[0_40px_90px_-20px_rgba(0,0,0,0.9)]">
              <img src={IMG.horizon} alt="A finished KLYR book cover forming from light" className="w-full aspect-[2/3] object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold-200/20 rounded-xl" />
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-[#080E21]" />
      </section>

      <FoundingOffer onGetStarted={onGetStarted} />

      {/* ========================================================== GALLERY */}
      <section className="bg-[#050915] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 mb-10 text-center">
          <div className="font-ui text-xs uppercase tracking-[0.3em] font-semibold text-gold-400 mb-3">Show, Don't Tell</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-50 leading-[1.12]">
            Real Books. <span className="gold-text italic font-normal">Every Genre, Every Mood.</span>
          </h2>
          <p className="mt-4 font-read text-slate-400 max-w-xl mx-auto">
            Sample covers across genres. Hover to pause and feel the range.
          </p>
        </div>
        <div className="klyr-marquee relative"
          style={{ maskImage: "linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)" }}>
          <div className="klyr-marquee-track py-2">
            {[...GALLERY, ...GALLERY].map((item, i) => <GalleryCard key={i} item={item} />)}
          </div>
        </div>
      </section>

      {/* ===================================================== NARRATIVE ARC */}
      <section className="relative bg-[#080E21] py-24 sm:py-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full"
          style={{ background: "radial-gradient(ellipse at center, rgba(229,193,88,0.10) 0%, rgba(8,14,33,0) 70%)" }} aria-hidden />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8 text-center mb-16">
          <div className="font-ui text-xs uppercase tracking-[0.3em] font-semibold text-gold-400 mb-5">From Idea to Finished Book</div>
          <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="font-display text-2xl sm:text-4xl font-medium text-slate-50 leading-[1.25]">
            Every great book starts with one idea. <span className="gold-text italic">KLYR turns yours into a finished, published book.</span>
          </motion.p>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8">
          <div className="absolute left-[27px] sm:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-gold-500/40 via-gold-500/20 to-transparent" aria-hidden />
          <div className="space-y-10">
            {ARC.map((s, i) => (
              <motion.div key={s.phase} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className={`relative flex items-start gap-6 sm:gap-10 ${i % 2 ? "sm:flex-row-reverse sm:text-right" : ""}`}>
                <div className="relative z-10 shrink-0 w-14 h-14 rounded-full bg-[#050915] border-2 border-gold-400/60 flex items-center justify-center sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                  <span className="font-display text-lg font-bold text-gold-300">{s.phase.split(" ")[0]}</span>
                </div>
                <div className={`flex-1 navy-card rounded-2xl p-6 sm:w-[calc(50%-2.5rem)] ${i % 2 ? "sm:mr-auto" : "sm:ml-auto"}`}>
                  <div className="font-ui text-[11px] uppercase tracking-[0.22em] text-gold-400/80 mb-2">{s.phase}</div>
                  <h3 className="font-display text-xl font-semibold text-slate-50">{s.title}</h3>
                  <p className="mt-2 font-read text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ WHAT KLYR MAKES POSSIBLE */}
      <section id="possible" className="scroll-mt-20 bg-[#050915] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-ui text-xs uppercase tracking-[0.3em] font-semibold text-gold-400 mb-3">What KLYR Makes Possible</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-50 leading-[1.12]">
              One Studio. <span className="gold-text italic font-normal">Infinite Formats.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {GENRES.map((g, i) => (
              <motion.div key={g.name} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
                whileHover={{ y: -6 }}
                className="group navy-card rounded-2xl p-6 border border-gold-500/10 hover:border-gold-400/40 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-gold-400/10 border border-gold-400/25 flex items-center justify-center group-hover:border-gold-400/60 transition-colors">
                    <g.icon className="w-5 h-5 text-gold-300" strokeWidth={1.6} />
                  </div>
                  <span className="font-ui text-[10px] uppercase tracking-[0.18em] text-gold-200/80 border border-gold-500/20 rounded-full px-2.5 py-1">{g.badge}</span>
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-50 leading-snug">{g.name}</h3>
                <p className="mt-2 font-read text-sm text-slate-400 leading-relaxed">{g.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================== CINEMATIC CTA */}
      <section className="relative bg-[#080E21] py-24 sm:py-28 overflow-hidden border-t border-gold-500/10">
        <div className="pointer-events-none absolute inset-0" aria-hidden
          style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(229,193,88,0.18) 0%, rgba(8,14,33,0) 60%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="font-display text-3xl sm:text-5xl font-semibold text-slate-50 leading-tight">
            Ready to Turn Your Spark <span className="gold-text italic font-normal">into a World?</span>
          </motion.h2>
          <p className="mt-5 font-read text-lg text-slate-400 max-w-xl mx-auto">
            Join hundreds of creators who transformed raw ideas into published books with KLYR Studio.
          </p>
          <button data-testid="final-cta-button" onClick={onGetStarted}
            className="mt-9 gold-pill rounded-full px-9 py-4 font-ui text-base inline-flex items-center gap-2">
            Enter the Studio <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-5 font-ui text-sm text-slate-500">{`"Your story deserves more than just an idea. It deserves KLYR."`}</div>
        </div>
      </section>

      <MarketingFooter onNavigate={handleNav} onHome={navHome} onGetStarted={onGetStarted} />
    </div>
  );
};
